import useCurrencyStore from '@/store/useCurrencyStore';
import { ReportsService } from '@/services/reports.service';
import { getDefaultDate } from '@/utils/dateHelpers';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  parseAsArrayOf,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { MULTI_VALUED_FILTER_KEYS } from '../constants';

export interface UtmFilterParams {
  [key: string]: string | string[] | undefined;
}

const extractFilterUuid = (value: string): string =>
  value.includes(':::') ? value.split(':::')[1] : value;

const extractUuids = (values?: string[] | null): string[] | undefined => {
  if (!values?.length) return undefined;
  return values.map(extractFilterUuid);
};

export function useUtmPanel() {
  const { t } = useTranslation(['reports', 'common', 'sales']);
  const currency = useCurrencyStore((s) => s.currency);

  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [productsFilter, setProductsFilter] = useQueryState(
    'products',
    parseAsArrayOf(parseAsString),
  );
  const [offersFilter, setOffersFilter] = useQueryState(
    'offers',
    parseAsArrayOf(parseAsString),
  );
  const [checkoutsFilter, setCheckoutsFilter] = useQueryState(
    'checkouts',
    parseAsArrayOf(parseAsString),
  );
  const [affiliatesFilter, setAffiliatesFilter] = useQueryState(
    'affiliates',
    parseAsArrayOf(parseAsString),
  );
  const [coproducersFilter, setCoproducersFilter] = useQueryState(
    'coproducers',
    parseAsArrayOf(parseAsString),
  );
  const [templatesFilter, setTemplatesFilter] = useQueryState(
    'templates',
    parseAsArrayOf(parseAsString),
  );
  const [searchParam, setSearchParam] = useQueryState(
    'search_param',
    parseAsString.withDefault('utm_campaign'),
  );
  const [searchValue, setSearchValue] = useQueryState('search_value', parseAsString);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  const dateRange = useMemo(
    (): DateRange => ({
      from: fromDate,
      to: toDate,
    }),
    [fromDate, toDate],
  );

  const filterParams = useMemo((): UtmFilterParams => {
    const params: UtmFilterParams = {};

    if (productsFilter?.length) params.products = productsFilter;
    if (offersFilter?.length) params.offers = offersFilter;
    if (checkoutsFilter?.length) params.checkouts = checkoutsFilter;
    if (affiliatesFilter?.length) params.affiliates = affiliatesFilter;
    if (coproducersFilter?.length) params.coproducers = coproducersFilter;
    if (templatesFilter?.length) params.templates = templatesFilter;
    if (searchParam) params.search_param = searchParam;
    if (searchValue) params.search_value = searchValue;

    return params;
  }, [
    productsFilter,
    offersFilter,
    checkoutsFilter,
    affiliatesFilter,
    coproducersFilter,
    templatesFilter,
    searchParam,
    searchValue,
  ]);

  const filterVisualParams = useMemo(() => {
    const result: Record<
      string,
      { label?: string; value?: string }[] | string[] | string
    > = {};

    Object.entries(filterParams).forEach(([key, value]) => {
      if (typeof value === 'string' && value === '') return;

      if (
        (MULTI_VALUED_FILTER_KEYS as readonly string[]).includes(key) &&
        Array.isArray(value)
      ) {
        const nonEmptyValues = value.filter((v) => v !== '');
        if (nonEmptyValues.length > 0) {
          result[key] = nonEmptyValues.map((item) => {
            if (typeof item === 'string' && item.includes(':::')) {
              const [label, val] = item.split(':::');
              return { label, value: val };
            }
            return { label: item, value: item };
          });
        }
        return;
      }

      if (typeof value === 'string' && value !== '') {
        result[key] = value;
      }
    });

    return result;
  }, [filterParams]);

  const crossFiltersObject = useMemo(() => {
    const obj: Record<string, string[]> = {};
    activeFilters.forEach((values, key) => {
      obj[key] = Array.from(values);
    });
    return obj;
  }, [activeFilters]);

  const apiParams = useMemo(
    () => ({
      from: fromDate?.toISOString() ?? '',
      to: toDate?.toISOString() ?? '',
      products: extractUuids(productsFilter),
      offers: extractUuids(offersFilter),
      checkouts: extractUuids(checkoutsFilter),
      affiliates: extractUuids(affiliatesFilter),
      coproducers: extractUuids(coproducersFilter),
      templates: extractUuids(templatesFilter),
      searchParam: searchValue ? searchParam ?? undefined : undefined,
      searchValue: searchValue || undefined,
      filters:
        Object.keys(crossFiltersObject).length > 0
          ? crossFiltersObject
          : undefined,
      currency,
    }),
    [
      fromDate,
      toDate,
      productsFilter,
      offersFilter,
      checkoutsFilter,
      affiliatesFilter,
      coproducersFilter,
      templatesFilter,
      searchParam,
      searchValue,
      crossFiltersObject,
      currency,
    ],
  );

  const queryKey = useMemo(
    () => ['utm-panel', apiParams],
    [apiParams],
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: () => ReportsService.getUtmPanel(apiParams),
    enabled: !!fromDate && !!toDate,
    placeholderData: keepPreviousData,
  });

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from) setFromDate(range.from);
      if (range?.to) setToDate(range.to);
    },
    [setFromDate, setToDate],
  );

  const setFilterParams = useCallback(
    (params: UtmFilterParams | ((prev: UtmFilterParams) => UtmFilterParams)) => {
      const newParams =
        typeof params === 'function' ? params(filterParams) : params;

      setProductsFilter((newParams.products as string[]) || null);
      setOffersFilter((newParams.offers as string[]) || null);
      setCheckoutsFilter((newParams.checkouts as string[]) || null);
      setAffiliatesFilter((newParams.affiliates as string[]) || null);
      setCoproducersFilter((newParams.coproducers as string[]) || null);
      setTemplatesFilter((newParams.templates as string[]) || null);
      setSearchParam((newParams.search_param as string) || 'utm_campaign');
      setSearchValue((newParams.search_value as string) || null);
    },
    [
      filterParams,
      setProductsFilter,
      setOffersFilter,
      setCheckoutsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
      setSearchParam,
      setSearchValue,
    ],
  );

  const filterSetters: Record<string, (v: string[] | null) => void> = useMemo(
    () => ({
      products: setProductsFilter,
      offers: setOffersFilter,
      checkouts: setCheckoutsFilter,
      affiliates: setAffiliatesFilter,
      coproducers: setCoproducersFilter,
      templates: setTemplatesFilter,
    }),
    [
      setProductsFilter,
      setOffersFilter,
      setCheckoutsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ],
  );

  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      if (key === 'search_value') {
        setSearchValue(null);
        return;
      }

      const setter = filterSetters[key];
      if (setter) {
        const currentValues = filterParams[key] as string[];
        const newValues = currentValues?.filter((_, i) => i !== index);
        setter(newValues?.length ? newValues : null);
      }
    },
    [filterParams, filterSetters, setSearchValue],
  );

  const removeFilter = useCallback(
    (key: string) => {
      if (key === 'search_value' || key === 'search_param') {
        setSearchValue(null);
        return;
      }

      const setter = filterSetters[key];
      if (setter) {
        setter(null);
      }
    },
    [filterSetters, setSearchValue],
  );

  const clearGlobalFilters = useCallback(() => {
    setProductsFilter(null);
    setOffersFilter(null);
    setCheckoutsFilter(null);
    setAffiliatesFilter(null);
    setCoproducersFilter(null);
    setTemplatesFilter(null);
    setSearchValue(null);
  }, [
    setProductsFilter,
    setOffersFilter,
    setCheckoutsFilter,
    setAffiliatesFilter,
    setCoproducersFilter,
    setTemplatesFilter,
    setSearchValue,
  ]);

  const clearCrossFilters = useCallback(() => {
    setActiveFilters(new Map());
  }, []);

  const clearAllFilters = useCallback(() => {
    clearGlobalFilters();
    clearCrossFilters();
  }, [clearGlobalFilters, clearCrossFilters]);

  const allSelectedValues = useMemo(() => {
    const set = new Set<string>();
    activeFilters.forEach((values, key) => {
      values.forEach((v) => set.add(`${key}:${v}`));
    });
    return set;
  }, [activeFilters]);

  const handleToggleValue = useCallback((groupKey: string, value: string) => {
    setActiveFilters((prev) => {
      const next = new Map(prev);
      const existing = next.get(groupKey) || new Set();
      const updated = new Set(existing);
      if (updated.has(value)) updated.delete(value);
      else updated.add(value);
      if (updated.size === 0) next.delete(groupKey);
      else next.set(groupKey, updated);
      return next;
    });
  }, []);

  const crossFilterCount = useMemo(() => {
    let count = 0;
    activeFilters.forEach((v) => {
      count += v.size;
    });
    return count;
  }, [activeFilters]);

  const globalFilterCount = useMemo(() => {
    let count = 0;
    MULTI_VALUED_FILTER_KEYS.forEach((key) => {
      const val = filterParams[key];
      if (Array.isArray(val)) count += val.length;
    });
    if (searchValue) count += 1;
    return count;
  }, [filterParams, searchValue]);

  const hasActiveGlobalFilters = globalFilterCount > 0;
  const hasActiveCrossFilters = crossFilterCount > 0;
  const hasAnyFilter = hasActiveGlobalFilters || hasActiveCrossFilters;

  const keyLabel: Record<string, string> = useMemo(
    () => ({
      products: t('common:products'),
      offers: t('common:offers'),
      checkouts: t('common:select_checkout'),
      affiliates: t('sales:affiliate'),
      coproducers: t('sales:co_producer'),
      templates: t('reports:template'),
      search_value: t('reports:utmPanel.parameter'),
    }),
    [t],
  );

  const productUuids = useMemo(
    () => extractUuids(productsFilter) ?? [],
    [productsFilter],
  );

  return {
    data,
    isLoading,
    isFetching,
    showSkeleton: isLoading && !data,
    summary: data?.summary,
    dateRange,
    handleDateRangeChange,
    filterParams,
    filterVisualParams,
    setFilterParams,
    removeFilterItem,
    removeFilter,
    clearGlobalFilters,
    clearCrossFilters,
    clearAllFilters,
    keyLabel,
    searchParam: searchParam ?? 'utm_campaign',
    searchValue: searchValue ?? '',
    setSearchParam,
    setSearchValue,
    hasActiveGlobalFilters,
    hasActiveCrossFilters,
    hasAnyFilter,
    globalFilterCount,
    crossFilterCount,
    showAdvanced,
    setShowAdvanced,
    activeFilters,
    allSelectedValues,
    handleToggleValue,
    productUuids,
    currency,
  };
}
